import React, { useState, useEffect } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	CircularProgress,
	TextField,
	InputAdornment,
	IconButton,
	Switch
} from '@material-ui/core';

import { Visibility, VisibilityOff } from '@material-ui/icons';

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import {
	usePanelStyles,
	ModalTitle,
	FormField,
	FormGrid,
	SwitchRow,
} from "../QueueIntegrationModal/panelKit";

const CompanySchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Too Short!")
		.max(50, "Too Long!")
		.required("Nome é obrigatório"),
	email: Yup.string().email("Email é inválido").required("E-mail é obrigatório"),
	numberAttendants: Yup.number(),
	numberConections: Yup.number(),
});

const CompanyModal = ({ open, onClose, companyId }) => {
	const panel = usePanelStyles();

	const initialState = {
		name: "",
		email: "",
		passwordDefault: "",
		numberAttendants: 1,
		numberConections: 1,
		status: false
	};

	const [company, setCompany] = useState(initialState);
	const [showPassword, setShowPassword] = useState(false);

	useEffect(() => {
		const fetchCompany = async () => {
			if (!companyId) return;
			try {
				const { data } = await api.get(`/companies/listPlan/${companyId}`);
				setCompany(prevState => {
					return { ...prevState, ...data };
				});
			} catch (err) {
				toastError(err);
			}
		};

		fetchCompany();
	}, [companyId, open]);

	const handleClose = () => {
		onClose();
		setCompany(initialState);
	};

	const handleSaveCompany = async values => {
		const companyData = { ...values };
		try {
			if (companyId) {
				await api.put(`/companies/${companyId}`, companyData);
			} else {
				await api.post("/companies", companyData);
			}
			toast.success(i18n.t("companyModal.success"));
		} catch (err) {
			toastError(err);
		}
		handleClose();
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth="xs"
			fullWidth
			scroll="paper"
			aria-labelledby="form-dialog-title"
		>
			<ModalTitle
				id="form-dialog-title"
				title={
					companyId
						? `${i18n.t("companyModal.title.edit")}`
						: `${i18n.t("companyModal.title.add")}`
				}
				onClose={handleClose}
			/>
			<Formik
				initialValues={company}
				enableReinitialize={true}
				validationSchema={CompanySchema}
				onSubmit={(values, actions) => {
					setTimeout(() => {
						handleSaveCompany(values);
						actions.setSubmitting(false);
					}, 400);
				}}
			>
				{({ values, touched, errors, isSubmitting }) => (
					<Form className={panel.form}>
						<DialogContent>
							<FormGrid columns={1}>
								<FormField
									label={i18n.t("companyModal.form.name")}
									htmlFor="company-modal-name"
									error={touched.name && errors.name}
								>
									<Field
										as={TextField}
										id="company-modal-name"
										autoFocus
										name="name"
										error={touched.name && Boolean(errors.name)}
										variant="outlined"
										size="small"
										fullWidth
									/>
								</FormField>
								<FormField
									label={i18n.t("companyModal.form.email")}
									htmlFor="company-modal-email"
									error={touched.email && errors.email}
								>
									<Field
										as={TextField}
										id="company-modal-email"
										name="email"
										error={touched.email && Boolean(errors.email)}
										variant="outlined"
										size="small"
										fullWidth
									/>
								</FormField>
								<FormField
									label={i18n.t("companyModal.form.passwordDefault")}
									htmlFor="company-modal-password"
									error={touched.passwordDefault && errors.passwordDefault}
								>
									<Field
										as={TextField}
										id="company-modal-password"
										name="passwordDefault"
										variant="outlined"
										size="small"
										error={touched.passwordDefault && Boolean(errors.passwordDefault)}
										type={showPassword ? 'text' : 'password'}
										InputProps={{
											endAdornment: (
												<InputAdornment position="end">
													<IconButton
														size="small"
														edge="end"
														aria-label="Mostrar ou ocultar a senha"
														onClick={() => setShowPassword((e) => !e)}
													>
														{showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
													</IconButton>
												</InputAdornment>
											)
										}}
										fullWidth
									/>
								</FormField>
								<SwitchRow label={"Ativo"}>
									<Field
										as={Switch}
										color="primary"
										name="status"
										checked={values.status}
										inputProps={{ "aria-label": "Ativo" }}
									/>
								</SwitchRow>
							</FormGrid>
						</DialogContent>
						<DialogActions>
							<Button onClick={handleClose} disabled={isSubmitting}>
								{i18n.t("companyModal.buttons.cancel")}
							</Button>
							<Button
								type="submit"
								color="primary"
								disabled={isSubmitting}
								variant="contained"
								className={panel.busyWrap}
							>
								{companyId
									? `${i18n.t("companyModal.buttons.okEdit")}`
									: `${i18n.t("companyModal.buttons.okAdd")}`}
								{isSubmitting && (
									<CircularProgress size={20} className={panel.busy} />
								)}
							</Button>
						</DialogActions>
					</Form>
				)}
			</Formik>
		</Dialog>
	);
};

export default CompanyModal;
